import { camelizeKeys } from 'humps';
import originalFetch from 'isomorphic-fetch';
import fetchRetry from 'fetch-retry';
import { datadogRum } from '@datadog/browser-rum';
import { triggerAuthLogout } from '../providers/authLogoutTrigger';
import { isBffHardeningOn } from '../api/harness/browserHarnessFlag';

const fetch = fetchRetry(originalFetch);

const defaultRequestOptions = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

const parseErrorResponse = error => {
  return error.response
    .json()
    .catch(() => {
      const errors = { base: [error.response.statusText] };
      return Promise.resolve({ errors });
    })
    .then(camelizeKeys);
};

const dispatchLogoutOn401 = status => {
  if (status === 401) triggerAuthLogout();
};

const parseResponse = response => {
  if (!response.ok) {
    // With BFF hardening off the logout runs before the body is read, as on
    // the default branch.
    const logoutAfterBody = isBffHardeningOn();
    if (response.body && !logoutAfterBody) dispatchLogoutOn401(response.status);
    if (response.body) {
      return (
        response
          .json()
          // A gateway answers an outage with plain text ("no healthy
          // upstream"), not JSON. Keep the status so callers can classify it
          // instead of surfacing a SyntaxError with none.
          .catch(() => ({}))
          .then(body => {
            const { error, errors = [] } = body;
            // The verification page handles a 401 from the BFF's verification
            // auth, which names its auth_mode, without a portal-wide logout.
            if (logoutAfterBody && !body.auth_mode) {
              dispatchLogoutOn401(response.status);
            }
            const errorMessage =
              [...errors, error].filter(Boolean).join(', ') ||
              response.statusText;
            const customError = new Error(errorMessage);
            customError.response = response;
            customError.status = response.status;
            customError.body = body;
            throw customError;
          })
      );
    }
    dispatchLogoutOn401(response.status);
    const customError = new Error(response.statusText);
    customError.response = response;
    customError.status = response.status;
    throw customError;
  }

  return response.json();
};

const request = (path, options = {}) => {
  const requestOptions = { ...defaultRequestOptions, ...options };
  return fetch(path, requestOptions).then(parseResponse).then(camelizeKeys);
};

const poll = (fn, check, timeout, interval) => {
  const endTime = Number(new Date()) + (timeout || 120000);
  interval = interval || 500;

  const checkCondition = (resolve, reject) => {
    fn()
      .then(result => {
        if (check(result)) {
          resolve(result);
        } else if (Number(new Date()) < endTime) {
          setTimeout(checkCondition, interval, resolve, reject);
        } else {
          reject(new Error(`timed out for ${fn}`));
        }
      })
      .catch(error => error);
  };
  return new Promise(checkCondition);
};

const initializeDDRUM = (email, applicantId) => {
  datadogRum.setGlobalContextProperty('usr', {
    email,
    id: applicantId,
  });
};

export { parseErrorResponse, parseResponse, request, poll, initializeDDRUM };
